/**
 * Form Handler Module
 * Xử lý submit form, payment popup
 */

import * as StateManager from '../shared/state-manager.js';
import { validateForm } from '../shared/validator.js';
import { formatPrice, escapeHtml } from '../shared/utils.js';
import {
    generateFormDataHash,
    clearSubmittedHash,
    clearCoursesFromStorage,
    saveCustomerInfo
} from '../shared/storage-service.js';
import { getTrafficSourceForSubmission } from '../shared/traffic-tracker.js';

// Dependencies
let getCampaignDiscount, renderSelectedCourses, updateTotals;

export function injectDependencies(deps) {
    getCampaignDiscount = deps.getCampaignDiscount;
    renderSelectedCourses = deps.renderSelectedCourses;
    updateTotals = deps.updateTotals;
}

/**
 * Get questionnaire integration instance (if available)
 */
function getQuestionnaireIntegration() {
    const integration = window.KTVHVNQuestionnaireIntegration;
    if (!integration || typeof integration.getResponses !== 'function') {
        return null;
    }

    return integration;
}

/**
 * Get questionnaire submission payload from integration add-on (if available)
 */
function getQuestionnaireSubmissionPayload() {
    const integration = getQuestionnaireIntegration();
    if (!integration) {
        return {};
    }

    const questionnaireData = integration.getResponses();
    if (!questionnaireData || !questionnaireData.questionnaire_id) {
        return {};
    }

    return {
        questionnaire_id: questionnaireData.questionnaire_id,
        questionnaire_ids: JSON.stringify(questionnaireData.questionnaire_ids || []),
        questionnaire_responses: JSON.stringify(questionnaireData.responses || {}),
        questionnaire_bundle: JSON.stringify(questionnaireData.bundle || {}),
    };
}

/**
 * Merge questionnaire payload into submit payload
 */
function withQuestionnairePayload(payload) {
    return Object.assign({}, payload, getQuestionnaireSubmissionPayload());
}

/**
 * Get current questionnaire price from integration (fallback to global state)
 */
function getQuestionnairePriceForPdfDirect() {
    const integration = getQuestionnaireIntegration();
    if (integration && typeof integration.calculateQuestionnairePrice === 'function') {
        const calculated = parseFloat(integration.calculateQuestionnairePrice());
        if (!Number.isNaN(calculated) && Number.isFinite(calculated)) {
            return calculated;
        }
    }

    return parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
}

/**
 * Normalize text extracted from questionnaire labels
 */
function normalizeQuestionnaireText(value) {
    if (!value) {
        return '';
    }
    return String(value).replace(/\s+/g, ' ').trim();
}

/**
 * Extract question label text for PDF rows
 */
function getQuestionnaireQuestionText($question) {
    const $label = $question.children('label').first();
    if ($label.length) {
        const $clone = $label.clone();
        $clone.find('.questionnaire-required').remove();
        const text = normalizeQuestionnaireText($clone.text());
        if (text) {
            return text;
        }
    }

    const fallback = normalizeQuestionnaireText($question.attr('data-field-name'));
    return fallback || 'Giá chính xác';
}

/**
 * Extract selected option label text for checkbox/radio inputs
 */
function getQuestionnaireOptionLabel($input) {
    const $wrapperLabel = $input.closest('label');
    if ($wrapperLabel.length) {
        const $clone = $wrapperLabel.clone();
        $clone.find('.questionnaire-option-price, small').remove();
        const text = normalizeQuestionnaireText($clone.text());
        if (text) {
            return text;
        }
    }

    return normalizeQuestionnaireText($input.val());
}

/**
 * Build questionnaire line items for direct PDF generation
 */
function getQuestionnaireItemsForPdfDirect() {
    const integration = getQuestionnaireIntegration();
    if (!integration || !Array.isArray(integration.currentQuestionnaires) || !integration.currentQuestionnaires.length) {
        return [];
    }

    const items = [];

    integration.currentQuestionnaires.forEach((qid) => {
        const normalizedQid = parseInt(qid, 10);
        if (!normalizedQid) {
            return;
        }

        const $container = jQuery(`.ktvhvn-questionnaire-container[data-questionnaire-id="${normalizedQid}"]`);
        if (!$container.length) {
            return;
        }

        $container.find('.questionnaire-question[data-visibility="visible"]').each(function() {
            const $question = jQuery(this);
            const questionText = getQuestionnaireQuestionText($question);

            $question.find('.questionnaire-checkbox:checked').each(function() {
                const $checkbox = jQuery(this);
                const price = parseFloat($checkbox.attr('data-price')) || 0;
                if (price <= 0) {
                    return;
                }

                const answer = getQuestionnaireOptionLabel($checkbox);
                items.push({
                    question: questionText,
                    answer: answer,
                    price: price
                });
            });

            const $radio = $question.find('.questionnaire-radio:checked');
            if ($radio.length) {
                const radioPrice = parseFloat($radio.attr('data-price')) || 0;
                if (radioPrice > 0) {
                    items.push({
                        question: questionText,
                        answer: getQuestionnaireOptionLabel($radio),
                        price: radioPrice
                    });
                }
            }

            const $select = $question.find('.questionnaire-select');
            if ($select.length) {
                const $selectedOption = $select.find('option:selected');
                const selectedPrice = parseFloat($selectedOption.attr('data-price')) || 0;
                if (selectedPrice > 0) {
                    items.push({
                        question: questionText,
                        answer: normalizeQuestionnaireText($selectedOption.text()),
                        price: selectedPrice
                    });
                }
            }
        });
    });

    return items;
}

/**
 * Build confirmation popup data
 */
export function showRegistrationPopup() {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    const discountPercent = StateManager.getDiscountPercent();

    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    let courseListHtml = '<ul>';
    selectedCourses.forEach(course => {
        let courseInfo = course.name;
        if (course.selected_variation && course.selected_variation.formatted_attributes) {
            courseInfo += ' - ' + Object.values(course.selected_variation.formatted_attributes).join(', ');
        }
        courseListHtml += `<li>${escapeHtml(courseInfo)} (${formatPrice(course.final_price)})</li>`;
    });
    courseListHtml += '</ul>';

    let total = 0;
    selectedCourses.forEach(course => {
        total += parseFloat(course.final_price) || 0;
    });

    // Include questionnaire/add-on price (from questionnaire addon)
    // NOTE: This must match the calculation in ui-manager.js updateTotals()
    const questionnairePrice = parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
    total += questionnairePrice;

    const discountAmount = total * (discountPercent / 100);
    const finalTotal = total - discountAmount;

    const $cache = StateManager.getCache();
    jQuery('#ktvhvn-registration-info-list').html(courseListHtml);
    jQuery('#ktvhvn-popup-total').text(window.ktvhvnCourseReg.i18n.total_label + ': ' + formatPrice(finalTotal));
    jQuery('#ktvhvn-popup-name').text($cache.name.val().trim());
    jQuery('#ktvhvn-popup-phone').text($cache.phone.val().trim());
    jQuery('#ktvhvn-popup-email').text($cache.email.val().trim() || window.ktvhvnCourseReg.i18n.not_provided);
    jQuery('#ktvhvn-popup-note').text($cache.note.val().trim() || window.ktvhvnCourseReg.i18n.not_provided);

    jQuery('#ktvhvn-registration-popup').fadeIn(200);
}

/**
 * Submit AJAX registration (called from confirmation popup)
 */
export function handleSubmitRegistration() {
    const button = jQuery(this);
    const originalHtml = button.html();

    button.html(`<i class="dashicons dashicons-update dashicons-spin"></i> ${window.ktvhvnCourseReg.i18n.loading}`).prop('disabled', true);

    const trafficSource = getTrafficSourceForSubmission();
    const $cache = StateManager.getCache();
    const selectedCourses = StateManager.getSelectedCourses();
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    const payload = withQuestionnairePayload({
        action: 'ktvhvn_submit_registration',
        nonce: window.ktvhvnCourseReg.nonce,
        name: $cache.name.val().trim(),
        phone: $cache.phone.val().trim(),
        email: $cache.email.val().trim(),
        note: $cache.note.val().trim(),
        courses: JSON.stringify(selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price || 0,
            is_free_course: course.isFreeCourse || false,
        }))),
        traffic_source: JSON.stringify(trafficSource),
        selected_campaign_id: selectedCampaignId || '',
    });

    jQuery.post(window.ktvhvnCourseReg.ajax_url, payload)
        .done(response => {
            if (response.success) {
                alert(window.ktvhvnCourseReg.i18n.success);
                jQuery('#ktvhvn-registration-popup').hide();
                resetForm();
                clearCoursesFromStorage();
                clearSubmittedHash();

                if (response.data && response.data.redirect_url) {
                    window.location.href = response.data.redirect_url;
                }
            } else {
                alert((response.data && response.data.message) || window.ktvhvnCourseReg.i18n.error);
            }
        })
        .fail(() => {
            alert(window.ktvhvnCourseReg.i18n.error);
        })
        .always(() => {
            button.html(originalHtml).prop('disabled', false);
        });
}

/**
 * Direct submit registration without showing popup
 * Validates form, then submits via AJAX
 */
export function handleDirectSubmitRegistration() {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    const button = jQuery(this);
    const originalHtml = button.html();

    button.html(`<i class="dashicons dashicons-update dashicons-spin"></i> ${window.ktvhvnCourseReg.i18n.loading}`).prop('disabled', true);

    const trafficSource = getTrafficSourceForSubmission();
    const $cache = StateManager.getCache();
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    const payload = withQuestionnairePayload({
        action: 'ktvhvn_submit_registration',
        nonce: window.ktvhvnCourseReg.nonce,
        name: $cache.name.val().trim(),
        phone: $cache.phone.val().trim(),
        email: $cache.email.val().trim(),
        note: $cache.note.val().trim(),
        courses: JSON.stringify(selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price || 0,
            is_free_course: course.isFreeCourse || false,
        }))),
        traffic_source: JSON.stringify(trafficSource),
        selected_campaign_id: selectedCampaignId || '',
    });

    jQuery.post(window.ktvhvnCourseReg.ajax_url, payload)
        .done(response => {
            if (response.success) {
                alert(window.ktvhvnCourseReg.i18n.success);
                resetForm();
                clearCoursesFromStorage();
                clearSubmittedHash();

                if (response.data && response.data.redirect_url) {
                    window.location.href = response.data.redirect_url;
                }
            } else {
                alert((response.data && response.data.message) || window.ktvhvnCourseReg.i18n.error);
            }
        })
        .fail(() => {
            alert(window.ktvhvnCourseReg.i18n.error);
        })
        .always(() => {
            button.html(originalHtml).prop('disabled', false);
        });
}

/**
 * Direct submit registration and show payment popup
 */
export function handleDirectSubmitWithPayment(event, overrideAmount = null) {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    // Check if auto-submit is disabled for payment button
    const autoSubmitEnabled = window.ktvhvnCourseReg.auto_submit?.payment !== false;
    if (!autoSubmitEnabled) {
        // Skip AJAX submission, just show payment popup
        console.log('KTVHVN: Auto-submit disabled for payment, showing popup directly');
        showPaymentPopup(overrideAmount);
        return;
    }

    const button = jQuery(this);
    const originalHtml = button.html();

    // Generate hash of current form data
    const currentFormHash = generateFormDataHash();

    // Check if form data has changed since last submission
    const lastSubmittedHash = localStorage.getItem('ktvhvn_last_submitted_hash');

    if (lastSubmittedHash === currentFormHash) {
        // Form data hasn't changed - skip submission, show payment popup directly
        console.log('KTVHVN: Form data unchanged, skipping duplicate submission');
        showPaymentPopup(overrideAmount);
        return;
    }

    // Form data has changed or first submission - proceed with AJAX
    button.html(`<i class="dashicons dashicons-update dashicons-spin"></i> ${window.ktvhvnCourseReg.i18n.loading}`).prop('disabled', true);

    const trafficSource = getTrafficSourceForSubmission();
    const $cache = StateManager.getCache();
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    const payload = withQuestionnairePayload({
        action: 'ktvhvn_submit_registration',
        nonce: window.ktvhvnCourseReg.nonce,
        name: $cache.name.val().trim(),
        phone: $cache.phone.val().trim(),
        email: $cache.email.val().trim(),
        note: $cache.note.val().trim(),
        courses: JSON.stringify(selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price || 0,
            is_free_course: course.isFreeCourse || false,
        }))),
        traffic_source: JSON.stringify(trafficSource),
        selected_campaign_id: selectedCampaignId || '',
    });

    jQuery.post(window.ktvhvnCourseReg.ajax_url, payload)
        .done(response => {
            if (response.success) {
                // Save hash after successful submission
                localStorage.setItem('ktvhvn_last_submitted_hash', currentFormHash);

                // Don't reset form or clear courses - keep data for payment
                // Show payment popup immediately
                showPaymentPopup(overrideAmount);
            } else {
                alert((response.data && response.data.message) || window.ktvhvnCourseReg.i18n.error);
            }
        })
        .fail(() => {
            alert(window.ktvhvnCourseReg.i18n.error);
        })
        .always(() => {
            button.html(originalHtml).prop('disabled', false);
        });
}

/**
 * Show VietQR popup
 */
export function showPaymentPopup(overrideAmount = null) {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    const discountPercent = StateManager.getDiscountPercent();

    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    let total = 0;
    selectedCourses.forEach(course => {
        total += parseFloat(course.final_price) || 0;
    });

    // Include questionnaire/add-on price (from questionnaire addon)
    // NOTE: This must match the calculation in ui-manager.js updateTotals()
    const questionnairePrice = parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
    total += questionnairePrice;

    // Get freeCoursesDiscount from state (for "Mua 1 Tặng 1" and similar campaigns)
    const freeCoursesDiscount = StateManager.getFreeCoursesDiscount();
    // Use campaign discount if available, otherwise use bulk discount
    const campaignDiscount = getCampaignDiscount();
    const hasOverrideAmount = typeof overrideAmount === 'number' && !isNaN(overrideAmount);
    let finalTotal = hasOverrideAmount ? overrideAmount : total;

    if (hasOverrideAmount) {
        jQuery('#ktvhvn-payment-discount-notice').hide();
    } else if (freeCoursesDiscount > 0) {
        // ✅ FREE COURSES: Calculate total discount = freeCoursesDiscount + campaign additional discount
        let totalDiscount = freeCoursesDiscount;

        // Check if there's additional discount from campaign (e.g., free_item with extra %)
        const selectedCampaignId = StateManager.getSelectedCampaignId();
        const campaignsCache = StateManager.getCampaignsCache();
        if (selectedCampaignId && campaignsCache && campaignsCache.campaigns) {
            const selectedCampaign = campaignsCache.campaigns.find(c => c.id === selectedCampaignId);
            if (selectedCampaign && selectedCampaign.discount_type === 'free_item' && parseFloat(selectedCampaign.discount_value) > 0) {
                const additionalPercent = parseFloat(selectedCampaign.discount_value);
                const paidTotal = total - freeCoursesDiscount;
                const additionalDiscount = paidTotal * additionalPercent / 100;
                totalDiscount += additionalDiscount;
            }
        }

        finalTotal = total - totalDiscount;
        jQuery('#ktvhvn-payment-discount-notice').show();
    } else if (campaignDiscount > 0) {
        // Use campaign discount
        finalTotal = total - campaignDiscount;
        jQuery('#ktvhvn-payment-discount-notice').show();
    } else if (discountPercent > 0) {
        // Fallback to bulk discount if no campaigns
        const discountAmount = total * (discountPercent / 100);
        finalTotal = total - discountAmount;
        jQuery('#ktvhvn-payment-discount-notice').show();
    } else {
        jQuery('#ktvhvn-payment-discount-notice').hide();
    }

    // Sử dụng .html() để hiển thị đúng với "Liên Hệ"
    const priceText = formatPrice(finalTotal);
    // Nếu là HTML (có tag), giữ nguyên. Nếu là text, thay 'đ' thành 'VND'
    const displayPrice = priceText.includes('<span') ? priceText : priceText.replace('đ', ' VND');
    jQuery('#ktvhvn-payment-amount').html(displayPrice);

    const paymentCode = generatePaymentCode();
    jQuery('#ktvhvn-payment-code-text').text(paymentCode);

    const bankId = 'BIDV';
    const accountNo = '1261274211';
    const accountName = 'CONG TY CP DAU TU VA CONG NGHE VIET HUNG';
    const amount = Math.round(finalTotal);
    const addInfo = encodeURIComponent(paymentCode);

    const qrUrl = `https://img.vietqr.io/image/${bankId}-${accountNo}-compact2.png?amount=${amount}&addInfo=${addInfo}&accountName=${accountName}`;
    jQuery('#ktvhvn-qr-code-image').attr('src', qrUrl);

    jQuery('#ktvhvn-payment-popup').fadeIn(200);
}

/**
 * Direct submit but force fixed 500k amount for trial payment
 */
export function handleTrialPayment(event) {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    // Check if auto-submit is disabled for trial button
    const autoSubmitEnabled = window.ktvhvnCourseReg.auto_submit?.trial !== false;
    if (!autoSubmitEnabled) {
        // Skip AJAX submission, just show payment popup with trial amount
        console.log('KTVHVN: Auto-submit disabled for trial, showing popup directly');
        showPaymentPopup(500000);
        return;
    }

    // Call the main handler with trial amount
    return handleDirectSubmitWithPayment.call(this, event, 500000);
}

/**
 * Handle print quote - submit registration and generate PDF
 */
export function handlePrintQuote() {
    if (!validateForm()) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    if (selectedCourses.length === 0) {
        alert(window.ktvhvnCourseReg.i18n.course_required);
        return;
    }

    const $btn = jQuery('#ktvhvn-print-quote-btn');
    const originalHtml = $btn.html();
    $btn.prop('disabled', true).html('<i class="fa fa-spinner fa-spin"></i> Đang tạo PDF...');

    const $cache = StateManager.getCache();

    // Check if auto-submit is disabled for print quote button
    const autoSubmitEnabled = window.ktvhvnCourseReg.auto_submit?.print_quote !== false;

    if (!autoSubmitEnabled) {
        // Direct mode: generate PDF without saving registration
        console.log('KTVHVN: Auto-submit disabled for print quote, using direct mode');
        generatePdfQuoteDirect($cache, selectedCourses, $btn, originalHtml);
        return;
    }

    // Standard mode: submit registration first, then generate PDF
    const trafficSource = getTrafficSourceForSubmission();
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    const payload = withQuestionnairePayload({
        action: 'ktvhvn_submit_registration',
        nonce: window.ktvhvnCourseReg.nonce,
        name: $cache.name.val().trim(),
        phone: $cache.phone.val().trim(),
        email: $cache.email.val().trim(),
        note: $cache.note.val().trim(),
        courses: JSON.stringify(selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price || 0,
            is_free_course: course.isFreeCourse || false,
        }))),
        traffic_source: JSON.stringify(trafficSource),
        selected_campaign_id: selectedCampaignId || '',
    });

    jQuery.post(window.ktvhvnCourseReg.ajax_url, payload)
        .done(response => {
            if (response.success && response.data && response.data.registration_id) {
                // Generate PDF with the registration ID
                generatePdfQuote(response.data.registration_id);
            } else {
                alert((response.data && response.data.message) || window.ktvhvnCourseReg.i18n.error);
            }
        })
        .fail(() => {
            alert(window.ktvhvnCourseReg.i18n.error);
        })
        .always(() => {
            $btn.prop('disabled', false).html(originalHtml);
        });
}

/**
 * Generate PDF quote using registration ID
 */
function generatePdfQuote(registrationId) {
    jQuery.post(window.ktvhvnCourseReg.ajax_url, {
        action: 'ktvhvn_generate_pdf_quote',
        nonce: window.ktvhvnCourseReg.nonce,
        registration_id: registrationId
    })
    .done(response => {
        if (response.success && response.data && response.data.file_url) {
            window.open(response.data.file_url, '_blank');
        } else {
            alert('Có lỗi khi tạo PDF: ' + (response.data?.message || 'Unknown error'));
        }
    })
    .fail(() => {
        alert('Không thể tạo PDF. Vui lòng thử lại.');
    });
}

/**
 * Generate PDF quote directly without saving registration
 * Sends complete form data including discount and questionnaire info
 */
function generatePdfQuoteDirect($cache, selectedCourses, $btn, originalHtml) {
    // Get discount information from DOM (populated by campaign selection)
    let discountAmount = 0;
    let campaignName = '';
    
    // Try to get discount amount from the discount display element
    if ($cache.discountAmount && $cache.discountAmount.length) {
        const discountText = $cache.discountAmount.text().replace(/[^\d]/g, '');
        discountAmount = parseInt(discountText, 10) || 0;
    }
    
    // Try to get campaign name from the campaign name display element
    if ($cache.discountCampaignName && $cache.discountCampaignName.length) {
        campaignName = $cache.discountCampaignName.text().trim();
    }
    
    // Collect questionnaire data from current DOM state to keep PDF in sync with UI.
    const questionnairePrice = getQuestionnairePriceForPdfDirect();
    const questionnaireData = getQuestionnaireItemsForPdfDirect();
    
    jQuery.post(window.ktvhvnCourseReg.ajax_url, {
        action: 'ktvhvn_generate_pdf_quote',
        nonce: window.ktvhvnCourseReg.nonce,
        direct_mode: '1',
        name: $cache.name.val().trim(),
        phone: $cache.phone.val().trim(),
        email: $cache.email.val().trim(),
        note: $cache.note.val().trim(),
        courses: JSON.stringify(selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price || 0,
        }))),
        // Include discount data for PDF generation
        discount_amount: discountAmount,
        campaign_name: campaignName,
        // Include questionnaire/giá chính xác data
        questionnaire_price: questionnairePrice,
        questionnaire_data: questionnaireData.length ? JSON.stringify(questionnaireData) : ''
    })
    .done(response => {
        if (response.success && response.data && response.data.file_url) {
            window.open(response.data.file_url, '_blank');
        } else {
            alert('Có lỗi khi tạo PDF: ' + (response.data?.message || 'Unknown error'));
        }
    })
    .fail(() => {
        alert('Không thể tạo PDF. Vui lòng thử lại.');
    })
    .always(() => {
        $btn.prop('disabled', false).html(originalHtml);
    });
}

/**
 * Payment reference generator
 */
function generatePaymentCode() {
    const $cache = StateManager.getCache();
    const phone = $cache.phone.val().trim();
    const cleanPhone = phone.replace(/[\s\-\.]/g, '');

    // Lấy 6 số cuối của SĐT
    const last6Digits = cleanPhone.slice(-6);

    return `KTVHVN${last6Digits}`;
}

/**
 * Reset UI and form fields
 */
export function resetForm() {
    const $cache = StateManager.getCache();
    $cache.name.val('');
    $cache.phone.val('');
    $cache.email.val('');
    $cache.note.val('');

    StateManager.setSelectedCourses([]);
    StateManager.setDiscountPercent(0);

    renderSelectedCourses();
    updateTotals();
    clearCoursesFromStorage();
}
